import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { PenTool, Megaphone, BookOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { apiErr } from "../lib/api";

const ROLES = [
  { key: "creator", title: "Creator", icon: PenTool, desc: "Publish articles & Shorts, grow an audience, and access the Creator Studio.", dest: "/dashboard" },
  { key: "advertiser", title: "Advertiser", icon: Megaphone, desc: "Run ad campaigns across PictureThisInk and reach engaged readers.", dest: "/advertiser" },
  { key: "reader", title: "Reader", icon: BookOpen, desc: "Just here to explore — read stories, watch Shorts, and save a reading list.", dest: "/" },
];

export default function RoleSelect() {
  const { user, loading, selectRole } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");

  if (loading) return <div className="pti-container py-24 text-center text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.needs_role) return <Navigate to="/" replace />;

  const pick = async (r) => {
    setBusy(r.key);
    try {
      await selectRole(r.key);
      toast.success(`Welcome aboard as a ${r.title}!`);
      navigate(r.dest, { replace: true });
    } catch (e) {
      toast.error(apiErr(e));
      setBusy("");
    }
  };

  return (
    <div className="pti-container py-16 max-w-4xl" data-testid="role-select-page">
      <p className="text-pti-red font-black uppercase tracking-widest text-sm">Welcome, {user.display_name?.split(" ")[0] || "friend"}</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black mt-2">How will you use PictureThisInk?</h1>
      <p className="text-gray-600 mt-3 text-lg">Pick the experience that fits you. You’re signed in — this just sets up the right home base.</p>

      <div className="grid md:grid-cols-3 gap-6 mt-10">
        {ROLES.map((r) => {
          const Icon = r.icon;
          return (
            <button key={r.key} onClick={() => pick(r)} disabled={!!busy} data-testid={`role-${r.key}`}
              className="text-left border-2 border-gray-200 hover:border-pti-red p-6 transition-colors group disabled:opacity-60 bg-white">
              <div className="w-12 h-12 flex items-center justify-center bg-pti-black text-white group-hover:bg-pti-red transition-colors"><Icon size={22} /></div>
              <h2 className="font-display text-2xl font-bold mt-4">{r.title}</h2>
              <p className="text-gray-600 text-sm mt-2">{r.desc}</p>
              <span className="inline-block mt-4 text-sm font-bold text-pti-red">{busy === r.key ? "Setting up…" : "Continue →"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
